import { useEffect, useState } from "react";
import api from "../lib/api";

const TOKEN_KEY = "fld_admin_token";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function StatusPill({ status }) {
  const s = (status || "").toLowerCase();
  const color =
    s === "paid"
      ? "bg-[#7CFF00] text-black"
      : s === "new"
        ? "bg-[#7CFF00]/15 text-[#7CFF00] border border-[#7CFF00]/40"
        : "bg-zinc-800 text-zinc-300 border border-white/10";
  return (
    <span
      className={`font-mono-tactical text-[10px] tracking-widest uppercase px-2 py-0.5 ${color}`}
    >
      {status || "—"}
    </span>
  );
}

function AddonChips({ codes }) {
  if (!codes || codes.length === 0) return <span className="text-zinc-600 text-xs">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {codes.map((c) => (
        <span
          key={c}
          className="font-mono-tactical text-[10px] tracking-widest uppercase px-1.5 py-0.5 border border-[#7CFF00]/60 text-[#7CFF00]"
        >
          {c}
        </span>
      ))}
    </div>
  );
}

export default function AdminBookings() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (t) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/admin/bookings?token=${encodeURIComponent(t)}`);
      setItems(data);
      localStorage.setItem(TOKEN_KEY, t);
    } catch {
      setError("Unauthorized or unreachable. Check token.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitToken = (e) => {
    e.preventDefault();
    if (token.trim()) load(token.trim());
  };

  const bookings = items.filter((i) => i.type === "booking");
  const payments = items.filter((i) => i.type === "payment");

  return (
    <div data-testid="page-admin" className="container-fld py-16 min-h-screen">
      <div className="overline mb-3">// Admin Console</div>
      <h1 className="font-tactical text-4xl md:text-5xl font-bold uppercase text-white tracking-tight mb-8">
        Bookings <span className="text-[#7CFF00]">Dashboard</span>
      </h1>

      <form onSubmit={submitToken} className="flex gap-2 max-w-xl mb-8">
        <input
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="Admin token"
          data-testid="admin-token-input"
          className="flex-1 bg-[#0A0A0A] border border-white/15 px-4 py-3 text-white focus:border-[#7CFF00] focus:outline-none font-mono-tactical text-sm"
        />
        <button
          type="submit"
          data-testid="admin-load-btn"
          className="btn-primary px-6"
        >
          Load
        </button>
      </form>

      {error && (
        <p className="text-red-400 font-mono-tactical text-sm mb-6">// {error}</p>
      )}
      {loading && (
        <p className="text-[#7CFF00] font-mono-tactical text-sm mb-6">// Loading...</p>
      )}

      {/* Bookings */}
      <section className="mb-12" data-testid="admin-bookings-section">
        <h2 className="font-tactical text-2xl uppercase text-white tracking-widest mb-4">
          Training Bookings ({bookings.length})
        </h2>
        <div className="overflow-x-auto border border-white/10">
          <table className="w-full text-left text-sm">
            <thead className="bg-[#151515] text-[#7CFF00] font-mono-tactical text-[10px] tracking-widest uppercase">
              <tr>
                <th className="px-3 py-2">Received</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Phone</th>
                <th className="px-3 py-2">Package</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Add-Ons</th>
                <th className="px-3 py-2">Add-On $</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr
                  key={b.id}
                  data-testid={`admin-booking-${b.id}`}
                  className="border-t border-white/5 hover:bg-[#0F0F0F]"
                >
                  <td className="px-3 py-2 text-zinc-400">{fmtDate(b.created_at)}</td>
                  <td className="px-3 py-2 text-white">{b.name}</td>
                  <td className="px-3 py-2 text-zinc-300">{b.email}</td>
                  <td className="px-3 py-2 text-zinc-300">{b.phone}</td>
                  <td className="px-3 py-2 text-zinc-200">{b.course}</td>
                  <td className="px-3 py-2 text-zinc-300">{b.preferred_date}</td>
                  <td className="px-3 py-2"><AddonChips codes={b.addons} /></td>
                  <td className="px-3 py-2 text-[#7CFF00] font-mono-tactical">
                    {b.addon_price ? `+$${b.addon_price}` : "—"}
                  </td>
                  <td className="px-3 py-2"><StatusPill status={b.status} /></td>
                </tr>
              ))}
              {bookings.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-8 text-center text-zinc-500 font-mono-tactical">
                    // No bookings yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Payments */}
      <section data-testid="admin-payments-section">
        <h2 className="font-tactical text-2xl uppercase text-white tracking-widest mb-4">
          Stripe Payments ({payments.length})
        </h2>
        <div className="overflow-x-auto border border-white/10">
          <table className="w-full text-left text-sm">
            <thead className="bg-[#151515] text-[#7CFF00] font-mono-tactical text-[10px] tracking-widest uppercase">
              <tr>
                <th className="px-3 py-2">Paid On</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Items</th>
                <th className="px-3 py-2">Amount</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr
                  key={p.session_id}
                  className="border-t border-white/5 hover:bg-[#0F0F0F]"
                >
                  <td className="px-3 py-2 text-zinc-400">{fmtDate(p.updated_at)}</td>
                  <td className="px-3 py-2 text-zinc-300">{p.customer_email || "—"}</td>
                  <td className="px-3 py-2 text-zinc-200 max-w-md">
                    {(p.items_summary || []).join(" · ")}
                  </td>
                  <td className="px-3 py-2 text-[#7CFF00] font-mono-tactical">
                    ${p.amount ? p.amount.toFixed(2) : "—"}
                  </td>
                  <td className="px-3 py-2"><StatusPill status={p.payment_status} /></td>
                </tr>
              ))}
              {payments.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-zinc-500 font-mono-tactical">
                    // No payments yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
